import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";
import "./index.css";
import App from "./App.tsx";

function RootErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const message =
    error instanceof Error ? error.message : String(error ?? "Unknown error");

  return (
    <main
      role="alert"
      style={{
        boxSizing: "border-box",
        display: "grid",
        minHeight: "100vh",
        placeContent: "center",
        gap: "1rem",
        padding: "2rem",
        fontFamily: "Inter, system-ui, sans-serif",
        color: "#161616",
      }}
    >
      <h1 style={{ margin: 0 }}>Orbitas could not start</h1>
      <p style={{ maxWidth: "42rem", margin: 0 }}>
        The application encountered an initialization error. Check the preview
        environment configuration, then retry.
      </p>
      <pre
        style={{
          maxWidth: "min(42rem, 100%)",
          overflowWrap: "anywhere",
          whiteSpace: "pre-wrap",
          padding: "1rem",
          background: "#f4f4f4",
          border: "1px solid #e0e0e0",
        }}
      >
        {message}
      </pre>
      <button
        type="button"
        onClick={resetErrorBoundary}
        style={{
          justifySelf: "start",
          border: 0,
          padding: "0.75rem 1rem",
          color: "#fff",
          background: "#0f62fe",
          cursor: "pointer",
        }}
      >
        Retry
      </button>
    </main>
  );
}

// After a new deploy, the service worker may replace its pre-cache while
// the page still holds old chunk references. A reload picks up the new
// HTML + new SW cache. A sessionStorage guard prevents infinite loops.
// See https://vite.dev/guide/build.html#load-error-handling
window.addEventListener("vite:preloadError", () => {
  const key = "chunk-reload";
  if (!sessionStorage.getItem(key)) {
    sessionStorage.setItem(key, "1");
    window.location.reload();
  }
});

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary FallbackComponent={RootErrorFallback}>
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
